import React from 'react'

import {
  Box,
  Button,
  Flex,
  FormControl,
  FormErrorMessage,
  FormLabel,
  HStack,
  IconButton,
  Input,
  Menu,
  MenuButton,
  MenuItem,
  MenuItemProps,
  MenuList,
  Stack,
  Text,
} from '@chakra-ui/react'
import { Menu as MenuIcon } from 'src/components/icon'
import { useOpenPullDown } from 'src/hooks/use-open-pull-down'
import { useBreadcrumb } from 'src/hooks/useBreadcrumb'

import { IcfDocumentForList } from '../../icf-document/entity'
import { SortableMultiSelectRenewal } from '../components/sortable-multi-select-renewal'
import {
  UpdateIcfDocumentSetItem,
  IcfDocumentSet,
  IcfDocumentUidAndIndex,
} from '../entity'
import { IcfDocumentSetErrors } from '../validate'

type Props = {
  updateItem: UpdateIcfDocumentSetItem | undefined
  numberingId: IcfDocumentSet['numberingId'] | undefined
  approvedIcfDocuments: IcfDocumentForList[] | undefined
  canUpdate: boolean
  canDisable: boolean
  onChangeName: (value: string) => void
  onChangeSelectedDocuments: (items: IcfDocumentUidAndIndex[]) => void
  onValidate: () => boolean
  onSubmit: () => Promise<void>
  onCancel: () => void
  onDisable: () => Promise<void>
  errors: IcfDocumentSetErrors
  pullDownState: ReturnType<typeof useOpenPullDown>
  listPath: string
  detailPath: string
}

export const EditIcfDocumentSetRenewal: React.FC<Props> = ({
  updateItem,
  numberingId,
  approvedIcfDocuments,
  canUpdate,
  canDisable,
  onChangeName,
  onChangeSelectedDocuments,
  onValidate,
  onSubmit,
  onCancel,
  onDisable,
  errors,
  listPath,
  detailPath,
}) => {
  const pullDownItems: {
    name: string
    onClick: () => void
    color: MenuItemProps['color']
  }[] = [
    { name: 'キャンセル', onClick: onCancel, color: 'red.500' },
    ...(canDisable
      ? [{ name: '文書セットを無効化', onClick: onDisable, color: 'red.500' }]
      : []),
  ]

  const { renderBreadcrumb } = useBreadcrumb()

  if (!updateItem || !numberingId) {
    return null
  }

  return (
    <Flex h="full" direction="column" py="6">
      <Flex w="full" justify="space-between" wrap="wrap" rowGap={4} px="6">
        {renderBreadcrumb([
          {
            text: '文書セット一覧',
            link: listPath,
          },
          {
            text: '文書セット詳細',
            link: detailPath,
          },
          {
            text: '文書セット編集',
            isCurrentPage: true,
          },
        ])}
        <Flex flex="1" gap={4} justify="end">
          <Button
            isDisabled={!canUpdate}
            onClick={() => {
              if (!onValidate()) {
                return
              }

              onSubmit()
            }}
          >
            更新
          </Button>
          <Menu>
            <MenuButton
              as={IconButton}
              aria-label="メニューを開く"
              icon={<MenuIcon />}
              variant="customIconButtonGhost"
            />
            <MenuList>
              {pullDownItems.map(item => (
                <MenuItem
                  key={item.name}
                  onClick={item.onClick}
                  color={item.color}
                >
                  {item.name}
                </MenuItem>
              ))}
            </MenuList>
          </Menu>
        </Flex>
      </Flex>

      <Stack flex="1" spacing="8" mt="8" px="6">
        <HStack spacing="8">
          <Text fontSize="sm" fontWeight="medium" color="gray.600">
            文書セットID
          </Text>
          <Text>{numberingId}</Text>
        </HStack>

        <FormControl isInvalid={!!errors.name}>
          <FormLabel>文書セット名</FormLabel>
          <Input
            maxW="400px"
            value={updateItem.name}
            onChange={e => onChangeName(e.target.value)}
          />
          {errors.name && <FormErrorMessage>{errors.name}</FormErrorMessage>}
        </FormControl>

        {approvedIcfDocuments !== undefined && (
          <Box>
            <SortableMultiSelectRenewal
              icfDocuments={approvedIcfDocuments}
              onChangeSelectedDocuments={onChangeSelectedDocuments}
              selectedIcfDocuments={updateItem.icfDocuments}
              errorMessage={errors.documents}
            />
          </Box>
        )}
      </Stack>
    </Flex>
  )
}
